import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOwnerLayout } from "./OwnerLayout";
import { getOwnerProjectOverview } from "../../services/projectService";
import OwnerSignAIASection from "./components/OwnerSignAIASection";
import OwnerLienWaiversSection from "./components/OwnerLienWaiversSection";
import OwnerDrawHistorySection, {
  Draw,
  DrawSummary,
} from "./components/OwnerDrawHistorySection";
import {
  getOwnerDraws,
  getOwnerSignaturePendingDraws,
  getOwnerLienItems,
} from "../../services/drawService";
import { getProgressReportsList } from "../../services/progressReportService";
import "./OwnerProjectDetail.scss";
import Sidebar from "../../Components/Sidebar/Sidebar";
import MessageIcon from "../../Assets/Img/MessageIcon";
import OverviewIcon from "../../Assets/Img/OverviewIcon";
import DrawRequestIcon from "../../Assets/Img/DrawRequestIcon";
import DocIcon from "../../Assets/Img/DocIcon";
import HistoryIcon from "../../Assets/Img/HistoryIcon";
import ProgressIcon from "../../Assets/Img/ProgressIcon";
import LineWaiverIcon from "../../Assets/Img/LineWaiverIcon";
import ConversationPanel from "Components/Common/ConversationPanel/ConversationPanel";
import OwnerOverviewSection from "./components/OwnerOverviewSection";
import OwnerProgressReportsSection from "./components/OwnerProgressReportsSection";
import OwnerDocumentsSection from "./components/OwnerDocumentsSection";
import {
  getLenderPreconReview,
  getLenderSovVersions,
} from "services/lenderService";
import PreconSection from "Pages/Bank/BankDetails/PreconSection";
import OwnerChangeOrdersSection from "./components/OwnerChangeOrdersSection";
import ChangeOrderIcon from "Assets/Img/ChangeOrderIcon";
import RequirementsAcknowledgedSection from "Pages/Admin/ProjectDetail/sections/RequirementsAcknowledgedSection";
import SOVSetup from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";
import ScheduleOfValuesIcon from "Assets/Img/ScheduleOfValuesIcon";
import RequirementsAcknowledgedIcon from "Assets/Img/RequirementsAcknowledgedIcon";
import PreConstructionReportIcon from "Assets/Img/PreConstructionReportIcon";
import Button from "Components/Common/Button/Button";

interface SovVersion {
  id: string;
  versionNumber: number;
  status: string;
}

interface OverviewProject {
  id?: string;
  name?: string;
  address?: string;
  status?: string;
  projectStage?: string;
  progressPercentage?: string;
  estCompletionDate?: string;
}

interface OverviewPhase {
  phase: string;
  phaseOrder: number;
  phaseWeight: number;
  phaseCscEstimation: number;
  items: {
    id: string;
    itemCode: string;
    itemLabel: string;
    itemWeight: number;
    cscEstimation: number;
    isFlagged: boolean;
    note: Record<string, string>;
  }[];
}

interface AmountWithCurrency {
  amount?: number;
  currency?: { code?: string; symbol?: string; name?: string };
}

export interface OverviewData {
  project?: OverviewProject;
  drawRequest?: {
    id?: string;
    drawNumber?: number;
    drawDate?: string | null;
    approvedAmount?: number | null;
    requestedAmount?: number | null;
  };
  report?: {
    id?: string;
    reportDate?: string;
    inspectionDate?: string;
    inspectorName?: string;
    inspectionSnapshot?: {
      phases?: OverviewPhase[];
      overallCscEstimation: number;
      lineItems?: {
        id: string;
        isFlagged: boolean;
        cscEstimation: number;
        aiaItem: { itemCode: number; itemName: string; itemLabel: string };
      }[];
    };
  };
  contractUpdatedValue?: AmountWithCurrency;
  totalDisbursedAmount?: AmountWithCurrency;
  balanceToFinish?: AmountWithCurrency;
  stakeholders?: {
    gc?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      companyName?: string;
    };
    lender?: { id?: string; companyName?: string; branchName?: string };
    admin?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      companyName?: string;
      phone?: string;
    };
  };
  recentActivities?: {
    activities: Array<{
      type: string;
      title: string;
      description: string | null;
      date: string;
      performedBy: string;
    }>;
    inspectionCount: number;
  };
}

export default function OwnerProjectDetail() {
  const { id, activeTab } = useParams<{ id: string; activeTab?: string }>();
  const navigate = useNavigate();
  const { setCrumbs } = useOwnerLayout();
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [draws, setDraws] = useState<Draw[]>([]);
  const [drawSummary, setDrawSummary] = useState<DrawSummary | null>(null);
  const [pendingDraw, setPendingDraw] = useState<any>(null);
  const [lienItemsData, setLienItemsData] = useState<
    Record<string, { lienItems: any[] }>
  >({});
  const [loadingSignAia, setLoadingSignAia] = useState(true);
  const [loadingDraws, setLoadingDraws] = useState(true);
  const [loadingLienWaivers, setLoadingLienWaivers] = useState(true);
  const [progressReports, setProgressReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [activeSection, setActiveSection] = useState(activeTab ?? "overview");
  const fetchedSections = useState<Set<string>>(() => new Set())[0];
  const bankName = overviewData?.stakeholders?.lender?.companyName ?? "Bank";

  const fetchOverview = (projectId: string) => {
    setLoadingOverview(true);
    (
      getOwnerProjectOverview(projectId) as Promise<{ data: any; error: any }>
    ).then(({ data, error }) => {
      if (!error && data) {
        const overview = data?.data ?? data;
        setOverviewData(overview);
        setCrumbs([
          { label: "My Projects", link: "/owner-dashboard" },
          { label: overview?.project?.name ?? "Project" },
        ]);
      }
      setLoadingOverview(false);
    });
  };

  useEffect(() => {
    if (!id) return;
    setCrumbs([
      { label: "My Projects", link: "/owner-dashboard" },
      { label: "Loading…" },
    ]);
    fetchOverview(id);
  }, [id]);

  useEffect(() => {
    if (!id || fetchedSections.has(activeSection)) return;
    fetchedSections.add(activeSection);

    if (activeSection === "sign-aia") {
      setLoadingSignAia(true);
      (
        getOwnerSignaturePendingDraws(id) as Promise<{ data: any; error: any }>
      ).then(({ data, error }) => {
        if (!error && data) setPendingDraw(data?.data ?? data);
        setLoadingSignAia(false);
      });
    } else if (activeSection === "draws") {
      setLoadingDraws(true);
      (getOwnerDraws(id) as Promise<{ data: any; error: any }>).then(
        ({ data, error }) => {
          if (!error && data) {
            setDraws(data.data ?? []);
            setDrawSummary(data.addOns?.summary ?? null);
          }
          setLoadingDraws(false);
        },
      );
    } else if (activeSection === "reports") {
      setLoadingReports(true);
      (getProgressReportsList(id) as Promise<{ data: any; error: any }>).then(
        ({ data, error }) => {
          if (!error && data) setProgressReports(data?.data ?? []);
          setLoadingReports(false);
        },
      );
    } else if (activeSection === "lien-waivers") {
      setLoadingLienWaivers(true);
      (getOwnerLienItems(id) as Promise<{ data: any; error: any }>).then(
        ({ data, error }) => {
          if (!error && data) setLienItemsData(data?.data ?? {});
          setLoadingLienWaivers(false);
        },
      );
    } else if (activeSection === "precon") {
      setPreconLoading(true);
      (
        getLenderPreconReview(id, true) as Promise<{ data: any; error: any }>
      ).then(({ data, error }) => {
        if (!error && data?.data) setPreconData(data.data);
        if (error?.status === 404) setPreconNotFound(true);
        setPreconLoading(false);
      });
    }
  }, [activeSection, id]);

  const [preconData, setPreconData] = useState<any>(null);
  const [preconLoading, setPreconLoading] = useState(false);
  const [preconNotFound, setPreconNotFound] = useState(false);
  const [sovVersions, setSovVersions] = useState<SovVersion[]>([]);
  const [sovVersionsLoaded, setSovVersionsLoaded] = useState(false);
  const [aiaSigned, setAiaSigned] = useState(false);
  const [preferConvName, setPreferConvName] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!id) return;
    getLenderSovVersions(id).then(
      ({ data }: { data: { data?: SovVersion[] } | null }) => {
        if (data && Array.isArray(data.data)) {
          setSovVersions(
            [...data.data].sort((a, b) => b.versionNumber - a.versionNumber),
          );
        }
        setSovVersionsLoaded(true);
      },
    );
  }, [id]);

  useEffect(() => {
    if (activeSection !== "sov-version" || sovVersions.length === 0) return;
    const firstId = sovVersions[0].id;
    setActiveSection(`sov-version-${firstId}`);
    navigate(`/owner-project-detail/${id}/sov-version-${firstId}`, {
      replace: true,
    });
  }, [activeSection, sovVersions]);

  const showSection = (sectionId: string, convName?: string) => {
    setPreferConvName(convName);
    setActiveSection(sectionId);
    navigate(`/owner-project-detail/${id}/${sectionId}`, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Sidebar dummy data
  const ownerSections = [
    {
      label: "My Project",
      items: [
        { id: "overview", label: "Overview", icon: <OverviewIcon /> },
        {
          id: "sign-aia",
          label: "Sign Draw Request",
          icon: <DrawRequestIcon />,
          badge: { text: "View", variant: "view" },
        },
        { id: "draws", label: "Draw History", icon: <HistoryIcon /> },
        {
          id: "change-orders",
          label: "Change Orders",
          icon: <ChangeOrderIcon />,
        },
      ],
    },
    {
      label: "Reports",
      items: [
        { id: "reports", label: "Progress Reports", icon: <ProgressIcon /> },
        { id: "lien-waivers", label: "Lien Waivers", icon: <LineWaiverIcon /> },
        {
          id: "precon",
          label: "Pre-Construction",
          icon: <PreConstructionReportIcon />,
        },
      ],
    },
    {
      label: "Review SOV",
      items: [
        {
          id: "sov-version",
          label: "Schedule of Values",
          icon: <ScheduleOfValuesIcon />,
          subItems: sovVersions.map((v) => ({
            id: `sov-version-${v.id}`,
            versionNumber: v.versionNumber,
            label: `Version ${v.versionNumber}`,
            badge: {
              text: v.status === "superseded" ? "Inactive" : v.status,
              variant: v.status === "active" ? "view" : "",
            },
          })),
        },
      ],
    },
    {
      label: "Project Files",
      items: [
        {
          id: "documents",
          label: "Documents & Files",
          icon: <DocIcon />,
        },
        {
          id: "requirements-acknowledged",
          label: "Requirements Acknowledged",
          icon: <RequirementsAcknowledgedIcon />,
        },
      ],
    },
    {
      label: "Communication",
      items: [{ id: "messages", label: "Messages", icon: <MessageIcon /> }],
    },
  ];

  return (
    <div className="onwerDashboard">
      {/* NAV */}

      <div className="body-wrap">
        {/* SIDEBAR */}
        <Sidebar
          sections={ownerSections}
          activeSection={activeSection}
          onSelect={showSection}
          footerContent={
            overviewData?.stakeholders?.admin && (
              <div className="csc-contact-box">
                <div className="csc-contact-label">YOUR CSC CONTACT</div>
                <div className="csc-contact-name">
                  {overviewData.stakeholders.admin.firstName}{" "}
                  {overviewData.stakeholders.admin.lastName}
                </div>
                <div className="csc-contact-company">
                  {overviewData.stakeholders.admin.companyName}
                </div>
                {overviewData.stakeholders.admin.phone && (
                  <div className="csc-contact-phone">
                    {overviewData.stakeholders.admin.phone}
                  </div>
                )}
                <Button
                  type="button"
                  className="csc-contact-btn"
                  onClick={() => showSection("messages", "owner_admin")}
                >
                  Message CSC
                </Button>
              </div>
            )
          }
        />

        {/* MAIN */}
        <div className="main">
          {/* ══ OVERVIEW ══ */}
          <div
            className={`section ${activeSection === "overview" ? "active" : ""}`}
            id="sec-overview"
          >
            <OwnerOverviewSection
              overviewData={overviewData}
              bankName={bankName}
              aiaSigned={aiaSigned}
              showSection={showSection}
              loading={loadingOverview}
            />
          </div>

          {/* ══ SIGN AIA ══ */}
          <div
            className={`section ${activeSection === "sign-aia" ? "active" : ""}`}
            id="sec-sign-aia"
          >
            {id && (
              <OwnerSignAIASection
                projectId={id}
                pendingDraw={pendingDraw}
                aiaSigned={aiaSigned}
                onSigned={() => {
                  setAiaSigned(true);
                  if (id) fetchOverview(id);
                }}
                loading={loadingSignAia}
              />
            )}
          </div>

          {/* ══ DRAW HISTORY ══ */}
          <div
            className={`section ${activeSection === "draws" ? "active" : ""}`}
            id="sec-draws"
          >
            <OwnerDrawHistorySection
              draws={draws}
              summary={drawSummary}
              onSignNow={() => showSection("sign-aia")}
              loading={loadingDraws}
            />
          </div>

          {/* ══ PROGRESS REPORTS ══ */}
          <div
            className={`section ${activeSection === "reports" ? "active" : ""}`}
            id="sec-reports"
          >
            <OwnerProgressReportsSection
              reports={progressReports}
              loading={loadingReports}
              bankName={bankName}
              projectId={id ?? ""}
            />
          </div>

          {/* ══ LIEN WAIVERS ══ */}
          <div
            className={`section ${activeSection === "lien-waivers" ? "active" : ""}`}
            id="sec-lien-waivers"
          >
            <OwnerLienWaiversSection
              lienItemsData={lienItemsData}
              loading={loadingLienWaivers}
            />
          </div>

          {/* ══ CHANGE ORDERS ══ */}
          {activeSection === "change-orders" && (
            <OwnerChangeOrdersSection projectId={id ?? ""} />
          )}

          {/* ══ PRE-CONSTRUCTION ══ */}
          <div
            className={`section ${activeSection === "precon" ? "active" : ""}`}
            id="sec-precon"
          >
            <PreconSection
              data={preconData}
              loading={preconLoading}
              notFound={preconNotFound}
              projectId={id}
              preconReportId={preconData?.preconstructionReportId}
            />
          </div>

          {/* ══ SOV VERSION ══ */}
          {activeSection === "sov-version" &&
            sovVersionsLoaded &&
            sovVersions.length === 0 && (
              <div className="ds active">
                <EmptyState
                  icon={<NoDocuments />}
                  title="Schedule of Values Not Submitted"
                  subtitle="The GC has not yet submitted a Schedule of Values for this project."
                />
              </div>
            )}
          <SOVSetup
            isActive={activeSection.startsWith("sov-version-")}
            projectId={id}
            versionId={
              activeSection.startsWith("sov-version-")
                ? activeSection.replace("sov-version-", "")
                : undefined
            }
          />

          {/* ══ DOCUMENTS ══ */}
          <div
            className={`section ${activeSection === "documents" ? "active" : ""}`}
            id="sec-documents"
          >
            <OwnerDocumentsSection isActive={activeSection === "documents"} />
          </div>

          {/* ══ REQUIREMENTS ACKNOWLEDGED ══ */}
          {activeSection === "requirements-acknowledged" && (
            <RequirementsAcknowledgedSection projectId={id ?? ""} />
          )}

          {/* ══ MESSAGES ══ */}
          {id && (
            <ConversationPanel
              projectId={id}
              isActive={activeSection === "messages"}
              lender={overviewData?.stakeholders?.lender}
              projectName={overviewData?.project?.name ?? ""}
              preferConvName={preferConvName}
            />
          )}
        </div>
        {/* /main */}
      </div>
      {/* /body-wrap */}
    </div>
  );
}
